import { EventIdentifier, noop, TrackingEventType, useAnalytics } from '@capsicum/common';
import { IconButton, ModalService, Skeleton, SkeletonItem, useRouter } from '@capsicum/common-web';
import { PlusIcon, ReceiveArrowIcon, WithdrawArrowIcon } from '@capsicum/common-web/icons';
import { useLimitLedgerRemaining } from '@capsicum/features/topup/shared';
import { type EnhancedWalletCurrency, useWalletButtons, type WalletButton } from '@capsicum/features/wallet/shared';
import { useMe } from '@capsicum/socios-core';
import { type SVGProps } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { S } from './WalletActionBar.styled';
import { NothingToWithdrawDrawer } from './withdraw-drawers/NothingToWithdrawDrawer';

export type WalletActionBarProps = {
	defaultCurrency?: EnhancedWalletCurrency;
	isUsaRegulation: boolean;
	tokenBalance?: string;
};

export const WalletActionBar = ({ defaultCurrency, isUsaRegulation, tokenBalance }: WalletActionBarProps) => {
	const balance = defaultCurrency?.rawBalance;
	const { ledgerLimitData } = useLimitLedgerRemaining(defaultCurrency);
	const { walletButtons } = useWalletButtons({ balance, isUsaRegulation, ledgerLimitData, tokenBalance });
	const { push } = useRouter();
	const notEnoughToWithdraw = !!(!balance || Number(balance) <= 0) && !!(!tokenBalance || Number(tokenBalance) <= 0);
	const analytics = useAnalytics();
	const userId = useMe().me.externalUserId;

	const iconComponents: Record<WalletButton['icon'], (props: SVGProps<SVGSVGElement>) => JSX.Element> = {
		PlusIcon,
		ReceiveArrowIcon,
		WithdrawArrowIcon
	};

	const processRedirection = (path: string) => {
		if (path === 'withdraw') withdrawNavigation();
		else if (path === 'receive') receiveNavigation();
		else if (path === 'topup') topupwNavigation();
		else noop();
	};

	const withdrawNavigation = () => {
		notEnoughToWithdraw
			? ModalService.addModal({
					renderComponent: ({ close }) => (
						<NothingToWithdrawDrawer closeModal={close} testID="wallet-nothingToWithdraw-drawer" />
					)
				})
			: push(`/wallet/withdraw`);
	};

	const topupwNavigation = () => {
		analytics.sendEvent(TrackingEventType.track, EventIdentifier.TopUpWalletStart, {
			device_id: uuidv4(),
			user_id: userId
		});

		push(`/wallet/topup`);
	};

	const receiveNavigation = () => {
		push(`/wallet/receive`);
	};

	return (
		<S.Container>
			<S.ContainerWrapper>
				{walletButtons.map(button => {
					return (
						<S.IconContainer key={button.bottomText}>
							{button.state === 'pending' ? (
								<Skeleton>
									<SkeletonItem circle height="46px" width="46px" />
								</Skeleton>
							) : (
								<IconButton
									bottomText={button.bottomText}
									disabled={button.state === 'disabled'}
									forceClick
									icon={{ src: iconComponents[button.icon]! }}
									testID={button.testID}
									onClick={() => processRedirection(button.to)}
								/>
							)}
						</S.IconContainer>
					);
				})}
			</S.ContainerWrapper>
		</S.Container>
	);
};
